import { useEffect } from 'react';
import type { DadosClienteSolicitacao, VariavelEmailCliente } from '../../../models/emailCliente.modelos';

interface DadosClienteAberturaProps {
  campos: VariavelEmailCliente[];
  clienteNome: string;
  valores: DadosClienteSolicitacao;
  onChange: (valores: DadosClienteSolicitacao) => void;
  /** Sugestões da alocação escolhida (Responsável Raro / Responsável do Cliente), por chave. */
  sugestoes?: Record<string, string | null | undefined>;
}

/**
 * Bloco "Dados para o cliente" da abertura — só aparece quando o Cliente tem e-mail de
 * entrada/saída configurado pelo SI (ver EmailsClienteConfig) e pede só o que o Digital não
 * tem com segurança (gestores, contrato, projeto/equipe). O resto (CPF, endereço…) vem do
 * cadastro/pré-cadastro na hora do e-mail.
 */
export default function DadosClienteAbertura({ campos, clienteNome, valores, onChange, sugestoes = {} }: DadosClienteAberturaProps) {
  const chaveSugestoes = JSON.stringify(sugestoes);
  useEffect(() => {
    const preencher = campos.filter((c) => sugestoes[c.chave] && !valores[c.chave]);
    if (preencher.length === 0) return;
    onChange({ ...valores, ...Object.fromEntries(preencher.map((c) => [c.chave, sugestoes[c.chave] as string])) });
    // Só sugere quando a alocação (ou os campos) mudam — depois disso o campo é de quem abre.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chaveSugestoes, campos.length]);

  if (campos.length === 0) return null;

  return (
    <fieldset className="space-y-3 rounded-lg border border-amber-200 bg-amber-50/40 p-4">
      <legend className="px-1 text-sm font-medium text-gray-700">Dados para o cliente{clienteNome ? ` ${clienteNome}` : ''}</legend>
      <p className="text-xs text-gray-500">
        O SI repassa ao cliente para liberar acesso/máquina. Se não souber algum, deixe em branco — o SI pergunta depois.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        {campos.map((campo) => (
          <label key={campo.chave} className="block text-sm font-medium text-gray-700">
            {campo.label}
            <input
              className="input-primary mt-1 w-full"
              maxLength={500}
              value={valores[campo.chave] ?? ''}
              onChange={(event) => onChange({ ...valores, [campo.chave]: event.target.value })}
            />
            {campo.ajuda && <span className="mt-0.5 block text-[11px] font-normal text-gray-500">{campo.ajuda}</span>}
          </label>
        ))}
      </div>
    </fieldset>
  );
}
